import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MapPin, Search, LocateFixed, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

// Fix default icon paths (Vite doesn't bundle Leaflet's image assets by default)
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

interface Props {
  latitude: string;
  longitude: string;
  onChange: (lat: string, lng: string) => void;
  defaultCenter?: [number, number];
  height?: string;
}

const ETHIOPIA_CENTER: [number, number] = [9.03, 38.74]; // Addis Ababa

const isValidLat = (n: number) => Number.isFinite(n) && n >= -90 && n <= 90;
const isValidLng = (n: number) => Number.isFinite(n) && n >= -180 && n <= 180;

function ClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, Math.max(map.getZoom(), 13));
  }, [center[0], center[1]]);
  return null;
}

export function MapLocationPicker({
  latitude,
  longitude,
  onChange,
  defaultCenter = ETHIOPIA_CENTER,
  height = '320px',
}: Props) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);
  const [reverseLoading, setReverseLoading] = useState(false);

  // Local draft state for lat/lng inputs so users can edit freely;
  // committed (with confirmation UI) when valid + changed
  const [latDraft, setLatDraft] = useState(latitude);
  const [lngDraft, setLngDraft] = useState(longitude);
  const [latError, setLatError] = useState<string | null>(null);
  const [lngError, setLngError] = useState<string | null>(null);

  // Keep drafts in sync when parent value changes (map click, search, geolocate)
  useEffect(() => { setLatDraft(latitude); setLatError(null); }, [latitude]);
  useEffect(() => { setLngDraft(longitude); setLngError(null); }, [longitude]);

  const lat = latitude ? Number(latitude) : null;
  const lng = longitude ? Number(longitude) : null;
  const hasMarker = lat !== null && lng !== null && isValidLat(lat as number) && isValidLng(lng as number);

  const center = useMemo<[number, number]>(
    () => (hasMarker ? [lat as number, lng as number] : defaultCenter),
    [hasMarker, lat, lng, defaultCenter[0], defaultCenter[1]],
  );

  const handlePick = (la: number, ln: number) => {
    if (!isValidLat(la) || !isValidLng(ln)) {
      toast.error('Invalid coordinates');
      return;
    }
    onChange(la.toFixed(6), ln.toFixed(6));
  };

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&addressdetails=1&countrycodes=et&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        handlePick(parseFloat(data[0].lat), parseFloat(data[0].lon));
        setResolvedAddress(data[0].display_name || null);
      } else {
        toast.error('No results found. Try a more specific address.');
      }
    } catch {
      toast.error('Search failed. Check your connection.');
    } finally {
      setSearching(false);
    }
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handlePick(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
      },
      () => {
        toast.error('Could not get your location');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  // Reverse geocode whenever the committed coordinates change
  const reverseDebounce = useRef<number | null>(null);
  useEffect(() => {
    if (!hasMarker) { setResolvedAddress(null); return; }
    if (reverseDebounce.current) window.clearTimeout(reverseDebounce.current);
    reverseDebounce.current = window.setTimeout(async () => {
      setReverseLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        const data = await res.json();
        setResolvedAddress(data?.display_name || null);
      } catch {
        // ignore
      } finally {
        setReverseLoading(false);
      }
    }, 600);
    return () => { if (reverseDebounce.current) window.clearTimeout(reverseDebounce.current); };
  }, [hasMarker, lat, lng]);

  // Manual lat/lng edit handlers
  const onLatDraftChange = (v: string) => {
    setLatDraft(v);
    if (v === '' || v === '-') { setLatError(null); return; }
    const n = Number(v);
    setLatError(isValidLat(n) ? null : 'Latitude must be between -90 and 90');
  };
  const onLngDraftChange = (v: string) => {
    setLngDraft(v);
    if (v === '' || v === '-') { setLngError(null); return; }
    const n = Number(v);
    setLngError(isValidLng(n) ? null : 'Longitude must be between -180 and 180');
  };

  const draftDiffers =
    latDraft !== latitude || lngDraft !== longitude;
  const draftValid =
    latDraft !== '' && lngDraft !== '' &&
    isValidLat(Number(latDraft)) && isValidLng(Number(lngDraft));

  const confirmDraft = () => {
    if (!draftValid) return;
    onChange(Number(latDraft).toFixed(6), Number(lngDraft).toFixed(6));
  };
  const resetDraft = () => {
    setLatDraft(latitude);
    setLngDraft(longitude);
    setLatError(null);
    setLngError(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
            placeholder="Search address (e.g. Bole Edna Mall, Addis Ababa)"
            className="pl-8"
          />
        </div>
        <Button type="button" variant="secondary" onClick={handleSearch} disabled={searching} className="gap-1.5">
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </Button>
        <Button type="button" variant="outline" onClick={handleLocate} disabled={locating} className="gap-1.5">
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
          Use my location
        </Button>
      </div>

      <div className="rounded-xl overflow-hidden border" style={{ height }}>
        <MapContainer center={center} zoom={hasMarker ? 14 : 6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
          />
          <ClickHandler onPick={handlePick} />
          {hasMarker && (
            <>
              <Recenter center={center} />
              <Marker
                position={[lat as number, lng as number]}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const m = e.target as L.Marker;
                    const p = m.getLatLng();
                    handlePick(p.lat, p.lng);
                  },
                }}
              />
            </>
          )}
        </MapContainer>
      </div>

      {/* Selected address */}
      {hasMarker && (
        <div className="flex items-start gap-2 text-xs rounded-md bg-muted/40 border px-3 py-2">
          <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-foreground">Selected location</div>
            {reverseLoading ? (
              <div className="text-muted-foreground flex items-center gap-1.5 mt-0.5">
                <Loader2 className="h-3 w-3 animate-spin" /> Resolving address…
              </div>
            ) : resolvedAddress ? (
              <div className="text-muted-foreground break-words mt-0.5">{resolvedAddress}</div>
            ) : (
              <div className="text-muted-foreground mt-0.5">No address found for these coordinates.</div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 text-primary" />
        <span>Click the map or drag the pin to set the exact location.</span>
      </div>

      {/* Manual lat/lng edits with confirmation */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Latitude</Label>
          <Input
            type="number" step="any" min={-90} max={90}
            value={latDraft}
            onChange={(e) => onLatDraftChange(e.target.value)}
            placeholder="e.g. 9.0300"
            aria-invalid={!!latError}
          />
          {latError && <p className="text-[11px] text-destructive mt-1">{latError}</p>}
        </div>
        <div>
          <Label className="text-xs">Longitude</Label>
          <Input
            type="number" step="any" min={-180} max={180}
            value={lngDraft}
            onChange={(e) => onLngDraftChange(e.target.value)}
            placeholder="e.g. 38.7400"
            aria-invalid={!!lngError}
          />
          {lngError && <p className="text-[11px] text-destructive mt-1">{lngError}</p>}
        </div>
      </div>

      {draftDiffers && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs">
          <span className="text-amber-800">
            You edited the coordinates manually. Confirm to move the pin.
          </span>
          <div className="flex gap-1.5">
            <Button type="button" size="sm" variant="ghost" onClick={resetDraft} className="h-7 gap-1 text-xs">
              <X className="h-3 w-3" /> Cancel
            </Button>
            <Button type="button" size="sm" onClick={confirmDraft} disabled={!draftValid} className="h-7 gap-1 text-xs">
              <Check className="h-3 w-3" /> Confirm
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
